import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Box,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import moment from "moment";

import { PERMISSIONS } from "../../constants";
import {
  getContractConfig,
  DEFAULT_CONTRACT_CONFIG,
  allNatureOptions,
} from "../../services/backend/ContractConfigService";
import { getContractField } from "../../functions";
import { getContracts } from "../../services/backend/ContractService";
import ChargeAccountTemplate from "../contract/ChargeAccountTemplate";
import ChargeAccountList from "./ChargeAccountList";
import FinancingFilters from "./FinancingFilters";
import FinancingTable from "./FinancingTable";

const Financing = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [natureFilter, setNatureFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showTemplate, setShowTemplate] = useState({ contract: null, payment: null });
  const [contractCfg, setContractCfg] = useState(DEFAULT_CONTRACT_CONFIG);

  useEffect(() => {
    (async () => setContractCfg(await getContractConfig()))();
  }, []);

  // Opciones del filtro por naturaleza: un financiero sin acceso completo solo puede
  // elegir entre sus naturalezas permitidas; ADMIN y ADMIN_CONTRACT ven todas las
  // del catálogo configurable (config/contracts, con las constantes como fallback).
  const hasFullAccess = user.roles?.includes("ADMIN") || user.roles?.includes("ADMIN_CONTRACT");
  const natureOptions = !hasFullAccess && user.roles?.includes("FINANCIER")
    ? (Array.isArray(user.allowedNatures) ? user.allowedNatures : [])
    : allNatureOptions(contractCfg);

  const getContractFieldWithNews = useCallback((key, limitDate) => {
    return getContractField(showTemplate.contract?.news, showTemplate.contract, limitDate)(key);
  }, [showTemplate]);

  const getContractsAsync = async () => {
    setLoading(true);
    try {
      const response = await getContracts(user, undefined, selectedYear, true);
      const allContracts = response.map((contract) => ({
        ...contract,
        approvedAt: contract.approvedAt ? moment(contract.approvedAt) : null,
        finishedAt: contract.finishedAt ? moment(contract.finishedAt) : null,
        startedAt: contract.startedAt ? moment(contract.startedAt) : null,
        createdAt: contract.createdAt ? moment(contract.createdAt) : null,
        updatedAt: contract.updatedAt ? moment(contract.updatedAt) : null,
        news: contract.news?.map((n) => ({
          ...n,
          startedAt: n.startedAt ? moment(n.startedAt) : null,
          finishedAt: n.finishedAt ? moment(n.finishedAt) : null,
        })),
        ...(contract.payments ? Object.keys(contract.payments)
          .forEach((key) => {
            if (contract.payments[key].payAt) {
              contract.payments[key].payAt = moment(new Date(contract.payments[key].payAt));
            }
          }) : {})
      }));

      setContracts(allContracts || []);
      setFilteredContracts(allContracts || []);
    } catch (error) {
      console.error("Error loading contracts:", error);
      setContracts([]);
      setFilteredContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term, status, nature = natureFilter) => {
    let filtered = [...contracts];

    if (status !== "ALL") {
      filtered = filtered.filter((contract) => {
        const effectiveStatus = getContractField(contract.news || [], contract)("status");
        return effectiveStatus === status;
      });
    }

    if (nature && nature !== "ALL") {
      filtered = filtered.filter((contract) => contract.nature === nature);
    }

    if (term) {
      const searchLower = term.toLowerCase();
      filtered = filtered.filter(
        (contract) =>
          contract.contractCode?.toLowerCase().includes(searchLower) ||
          contract.contractSubject?.toLowerCase().includes(searchLower) ||
          contract.contractor?.name?.toLowerCase().includes(searchLower) ||
          contract.contractor?.lastName?.toLowerCase().includes(searchLower) ||
          contract.contractorNuip?.toString().includes(searchLower) ||
          contract.supervisors?.some(
            (s) =>
              s.name?.toLowerCase().includes(searchLower) ||
              s.lastName?.toLowerCase().includes(searchLower)
          )
      );
    }

    setFilteredContracts(filtered);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    handleSearch(value, statusFilter);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    handleSearch(searchTerm, value, natureFilter);
  };

  const handleNatureFilterChange = (value) => {
    setNatureFilter(value);
    handleSearch(searchTerm, statusFilter, value);
  };

  useEffect(() => {
    if (!user.permissions.includes(PERMISSIONS.FINANCING.LIST)) {
      navigate("/");
      return;
    }

    getContractsAsync();
  }, [user, selectedYear, navigate]); // eslint-disable-line

  return (
    <Container maxWidth="lg" style={{ padding: 3, marginTop: 10 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          style={{ margin: 16 }}
          onClick={() => navigate("/")}
        >
          Volver
        </Button>
        Contratos Consolidados
      </Typography>

      <FinancingFilters
        searchTerm={searchTerm}
        handleSearchChange={handleSearchChange}
        statusFilter={statusFilter}
        handleStatusFilterChange={handleStatusFilterChange}
        natureFilter={natureFilter}
        handleNatureFilterChange={handleNatureFilterChange}
        natureOptions={natureOptions}
        filteredContracts={filteredContracts}
        contracts={contracts}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
      />

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          TabIndicatorProps={{ sx: { display: "none" } }}
          sx={{
            minHeight: 0,
            px: 1,
            pt: 1,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.95rem",
              minHeight: 40,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              border: "1px solid transparent",
              borderBottom: "none",
              marginRight: 0.5,
              transition: "all 0.2s",
              color: "text.secondary",
              "&.Mui-selected": {
                fontWeight: 600,
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
                border: "1px solid",
                borderColor: "divider",
                borderBottom: "1px solid #e8f5e9",
                marginBottom: -1,
                zIndex: 1,
              },
            },
          }}
        >
          <Tab label="Contratos" />
          <Tab label="Cuentas de Cobro" />
        </Tabs>

        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
          {activeTab === 0 && (
            <FinancingTable loading={loading} filteredContracts={filteredContracts} />
          )}

          {activeTab === 1 && (
            <ChargeAccountList
              contracts={filteredContracts}
              setShowTemplate={setShowTemplate}
            />
          )}
        </Box>
      </Paper>

      {showTemplate.contract
         && <ChargeAccountTemplate
          contract={{ ...showTemplate.contract }}
          getContractFieldWithNews={getContractFieldWithNews}
          payment={showTemplate.payment ?? undefined}
          open={!!(showTemplate.contract && showTemplate.payment)}
          onClose={() => setShowTemplate({ contract: null, payment: null })}
        />
      }
    </Container>
  );
};

export default Financing;
